import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import { REIMBURSE_METHODS } from '../../types/pettyCash';
import type { PettyCashProcessSettings, ReimburseMethod, ReimbursementView } from '../../types/pettyCash';
import type { ReimburseInput } from '../../services/pettyCashApi';
import { aboveReceiptsPaid, cleanDateInput, creditText, formatAmount, formatDate, formatDateTime, isSameBox, parseAmount, reimburseCredit, round2, todayIso } from '../../utils/pettyCash';

/**
 * A posted claim's reimbursement: how and when the holder was paid back, and the SAP top-up journal when the setup
 * posts one (debit the holder's cash account, credit the account of the way he was paid: the bank, or the box the
 * cash came from). Finance records it; a refused journal is posted again.
 */

const methodLabel = (m: string) => REIMBURSE_METHODS.find((x) => x.value === m)?.label || m;

interface CardProps {
  reimbursement: ReimbursementView | null;
  currency: string;
  /** The receipts SAP paid: a reimbursement recorded above it (before PRPO refused that) is flagged. */
  paidAmount: number;
  canReimburse: boolean;
  canRetry: boolean;
  busy: boolean;
  onRecord: () => void;
  onRetry: () => void;
}

/** The working days the holder waits show next to the claim's status (WaitingChip), not here. */
export const ReimbursementCard: React.FC<CardProps> = ({ reimbursement: r, currency, paidAmount, canReimburse, canRetry, busy, onRecord, onRetry }) => {
  const retryButton = canRetry ? (
    <Button size="small" color="inherit" onClick={onRetry} disabled={busy}>{busy ? 'Posting…' : 'Post again'}</Button>
  ) : null;
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>Reimbursement</Typography>
      <Stack spacing={1} alignItems="flex-start">
        {r ? (
          <>
            <Typography variant="body2">
              <strong>{formatAmount(r.amount)} {currency}</strong> paid back on {formatDate(r.date)} · {methodLabel(r.method)}
              {r.reference ? ` ${r.reference}` : ''}
            </Typography>
            {r.note ? <Typography variant="body2" color="text.secondary">{r.note}</Typography> : null}
            {aboveReceiptsPaid(r.amount, paidAmount) ? (
              <Alert severity="warning" sx={{ width: '100%' }}>
                This is {formatAmount(round2(r.amount - paidAmount))} {currency} more than the receipts SAP paid ({formatAmount(paidAmount)} {currency}).
              </Alert>
            ) : null}
            <Typography variant="caption" color="text.secondary">Recorded{r.byName ? ` by ${r.byName}` : ''} {formatDateTime(r.at)}</Typography>
            {r.sap?.status === 'Posting' ? (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <CircularProgress size={14} />
                <Typography variant="body2">Posting the top-up journal in SAP…</Typography>
                {retryButton}
              </Stack>
            ) : null}
            {r.sap?.status === 'Posted' ? (
              <Typography variant="body2">Top-up journal entry {r.sap.number || r.sap.entry || ''} in SAP</Typography>
            ) : null}
            {r.sap && r.credit ? (
              <Typography variant="caption" color="text.secondary">Credit {creditText(r.credit)}</Typography>
            ) : null}
            {r.sap?.status === 'Failed' ? (
              <Alert severity="error" action={retryButton} sx={{ width: '100%' }}>
                SAP refused the top-up journal{r.sap.error ? `: ${r.sap.error}` : ''}
              </Alert>
            ) : null}
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">Not reimbursed yet.</Typography>
            {canReimburse ? <Button size="small" onClick={onRecord} disabled={busy}>Record reimbursement</Button> : null}
          </>
        )}
      </Stack>
    </Paper>
  );
};

interface DialogProps {
  open: boolean;
  currency: string;
  /** The receipts SAP paid: the default amount, and the most the holder can be paid back. */
  paidAmount: number;
  /** The setup posts the top-up journal in SAP. */
  postInSap: boolean;
  /** The bank account, and the account Cash, Cheque or Other credits instead (Petty Cash Setup › Settings). */
  accounts: Pick<PettyCashProcessSettings, 'reimburseBankAccount' | 'reimburseBankAccountName' | 'reimburseAccounts'>;
  cashAccount: string | null;
  auxiliary: string | null;
  onClose: () => void;
  onSave: (input: ReimburseInput) => Promise<void>;
}

export const ReimbursementDialog: React.FC<DialogProps> = ({
  open, currency, paidAmount, postInSap, accounts, cashAccount, auxiliary, onClose, onSave,
}) => {
  const [amountText, setAmountText] = useState('');
  const [date, setDate] = useState(todayIso());
  const [method, setMethod] = useState<ReimburseMethod>('BankTransfer');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setAmountText(paidAmount > 0 ? String(round2(paidAmount)) : '');
    setDate(todayIso());
    setMethod('BankTransfer');
    setReference('');
    setNote('');
    setError(null);
    setBusy(false);
  }, [open, paidAmount]);

  const needsReference = !!REIMBURSE_METHODS.find((m) => m.value === method)?.needsReference;
  // The account this way of paying credits; the backend refuses a top-up from the holder's own box.
  const credit = postInSap ? reimburseCredit(accounts, method) : null;
  const ownBox = !!credit && isSameBox({ account: cashAccount, auxiliary }, credit);
  // Never more than the claim took out of the box; the backend refuses it too.
  const tooMuch = aboveReceiptsPaid(parseAmount(amountText), paidAmount);
  const maxText = `${formatAmount(paidAmount)}${currency ? ` ${currency}` : ''}`;

  const save = async () => {
    if (ownBox) { setError('This way of paying takes the money from the holder\'s own box. Choose another way of paying.'); return; }
    const amount = parseAmount(amountText);
    if (amount === null || amount <= 0) { setError('Type the amount paid back (above 0).'); return; }
    if (aboveReceiptsPaid(amount, paidAmount)) { setError(`The amount cannot be more than the claim: ${maxText}, the receipts SAP paid.`); return; }
    const day = cleanDateInput(date);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) { setError('Enter the date of the payment.'); return; }
    if (day > todayIso()) { setError('The date cannot be in the future.'); return; }
    if (needsReference && !reference.trim()) { setError(`Type the ${method === 'Cheque' ? 'cheque number' : 'transfer reference'}.`); return; }
    setBusy(true);
    setError(null);
    try {
      await onSave({ amount: round2(amount), date: day, method, reference: reference.trim() || null, note: note.trim() || null });
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'The reimbursement could not be recorded');
    } finally {
      setBusy(false);
    }
  };

  const creditAccount = credit ? creditText(credit) : 'the bank account set in Petty Cash Setup';
  const cash = cashAccount ? `${cashAccount}${auxiliary ? ` with the holder's auxiliary ${auxiliary}` : ''}` : 'the holder\'s cash account';
  const methodName = REIMBURSE_METHODS.find((m) => m.value === method)?.label || method;

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="pc-reimburse-title">
      <DialogTitle id="pc-reimburse-title">Record reimbursement</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            How the holder was paid back for this claim.{' '}
            {postInSap
              ? `PRPO then posts the top-up journal in SAP: debit ${cash}, credit ${creditAccount}.`
              : 'Nothing is posted in SAP: this records a payment made outside PRPO.'}
          </Typography>
          <TextField
            id="pc-reimburse-amount"
            label={`Amount ${currency}`.trim()}
            value={amountText}
            onChange={(e) => setAmountText(e.target.value.replace(/[^\d.,]/g, '').slice(0, 20))}
            inputProps={{ inputMode: 'decimal' }}
            error={tooMuch}
            helperText={tooMuch
              ? `More than the claim: at most ${maxText}, the receipts SAP paid`
              : `The receipts SAP paid: ${formatAmount(paidAmount)} (the most that can be paid back)`}
            required
            fullWidth
          />
          <TextField
            id="pc-reimburse-date"
            type="date"
            label="Payment date"
            value={date}
            onChange={(e) => setDate(cleanDateInput(e.target.value))}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: todayIso() }}
            required
            fullWidth
          />
          <TextField id="pc-reimburse-method" select label="Paid by" value={method} onChange={(e) => { setMethod(e.target.value as ReimburseMethod); setError(null); }} required fullWidth>
            {REIMBURSE_METHODS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </TextField>
          {ownBox ? (
            <Alert severity="warning">
              {methodName} reimbursements come from {credit ? creditText(credit) : ''}, this holder&apos;s own box. Choose another way of paying,
              or change the account in Petty Cash Setup › Settings.
            </Alert>
          ) : null}
          <TextField
            id="pc-reimburse-reference"
            label={method === 'Cheque' ? 'Cheque number' : method === 'BankTransfer' ? 'Transfer reference' : 'Reference'}
            value={reference}
            onChange={(e) => setReference(e.target.value.slice(0, 100))}
            required={needsReference}
            helperText={needsReference ? 'Required' : 'Optional'}
            fullWidth
          />
          <TextField id="pc-reimburse-note" label="Note" value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} multiline minRows={2} fullWidth />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={() => { void save(); }} disabled={busy || ownBox || tooMuch}>{busy ? 'Saving…' : 'Record reimbursement'}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};
